import type {
  AssetUploadResponse,
  AuthTokens,
  BillingPlanDto,
  BillingState,
  ChangeTemplateRequest,
  ConfirmPublishRequest,
  CreateOrderRequest,
  CreateOrderResponse,
  CreateSiteRequest,
  DomainList,
  OrderDto,
  PublishPlan,
  SaveDraftRequest,
  SessionUser,
  Site,
  SiteSettings,
  SiteSource,
  SiteVersion,
  TemplateManifest,
  TonProofPayload,
  TonProofVerifyRequest,
  Wallet,
} from '@tb/shared';
import { request, uploadFile } from './client';

export const api = {
  auth: {
    telegram: (initData: string) =>
      request<{ user: SessionUser; tokens: AuthTokens }>('/auth/telegram', {
        method: 'POST',
        body: { initData },
        anonymous: true,
      }),
    tonProofPayload: (signal?: AbortSignal) =>
      request<TonProofPayload>('/auth/ton-proof/payload', { ...(signal ? { signal } : {}) }),
    verifyWallet: (body: TonProofVerifyRequest) =>
      request<Wallet>('/auth/wallet/verify', { method: 'POST', body }),
    wallets: () => request<Wallet[]>('/auth/wallets'),
    disconnect: (walletId: string) =>
      request<{ ok: true }>(`/auth/wallets/${walletId}`, { method: 'DELETE' }),
  },

  domains: {
    list: (wallet: string) => request<DomainList>(`/domains?wallet=${encodeURIComponent(wallet)}`),
    refresh: (wallet: string) =>
      request<DomainList>(`/domains/refresh?wallet=${encodeURIComponent(wallet)}`, {
        method: 'POST',
      }),
  },

  templates: {
    list: () => request<TemplateManifest[]>('/templates', { anonymous: true }),
  },

  sites: {
    list: () => request<Site[]>('/sites'),
    get: (siteId: string) => request<Site>(`/sites/${siteId}`),
    create: (wallet: string, body: CreateSiteRequest) =>
      request<Site>(`/sites?wallet=${encodeURIComponent(wallet)}`, { method: 'POST', body }),
    remove: (siteId: string) => request<{ ok: true }>(`/sites/${siteId}`, { method: 'DELETE' }),

    changeTemplate: (siteId: string, body: ChangeTemplateRequest) =>
      request<Site>(`/sites/${siteId}/template`, { method: 'POST', body }),
    saveDraft: (siteId: string, body: SaveDraftRequest) =>
      request<SiteVersion>(`/sites/${siteId}/draft`, { method: 'PUT', body }),
    preview: (siteId: string, body: SaveDraftRequest) =>
      request<string>(`/sites/${siteId}/preview`, { method: 'POST', body, raw: true }),

    versions: (siteId: string) => request<SiteVersion[]>(`/sites/${siteId}/versions`),
    version: (siteId: string, versionId: string) =>
      request<SiteVersion>(`/sites/${siteId}/versions/${versionId}`),
    versionSource: (siteId: string, versionId: string) =>
      request<{
        id: string;
        number: number;
        status: string;
        source: SiteSource;
        settings: SiteSettings;
        assetIds: string[];
      }>(`/sites/${siteId}/versions/${versionId}/source`),
    restore: (siteId: string, versionId: string) =>
      request<SiteVersion>(`/sites/${siteId}/versions/${versionId}/restore`, { method: 'POST' }),
  },

  assets: {
    list: (siteId: string) => request<AssetUploadResponse[]>(`/sites/${siteId}/assets`),
    upload: (siteId: string, file: File) =>
      uploadFile<AssetUploadResponse>(`/sites/${siteId}/assets`, file),
    remove: (siteId: string, assetId: string) =>
      request<{ ok: true }>(`/sites/${siteId}/assets/${assetId}`, { method: 'DELETE' }),
  },

  publish: {
    start: (siteId: string) => request<SiteVersion>(`/sites/${siteId}/publish`, { method: 'POST' }),
    plan: (siteId: string, versionId: string) =>
      request<PublishPlan>(`/sites/${siteId}/versions/${versionId}/transaction`),
    confirm: (siteId: string, versionId: string, body: ConfirmPublishRequest) =>
      request<SiteVersion>(`/sites/${siteId}/versions/${versionId}/confirm`, {
        method: 'POST',
        body,
      }),
  },

  billing: {
    plans: () => request<BillingPlanDto[]>(`/billing/plans`),
    me: () => request<BillingState>(`/billing/me`),
    createOrder: (body: CreateOrderRequest) =>
      request<CreateOrderResponse>(`/billing/orders`, { method: 'POST', body }),
    order: (orderId: string) => request<OrderDto>(`/billing/orders/${orderId}`),
    confirm: (orderId: string) =>
      request<OrderDto>(`/billing/orders/${orderId}/confirm`, { method: 'POST' }),
  },

  submissions: {
    list: (siteId: string) =>
      request<{ id: string; fields: Record<string, string>; createdAt: string }[]>(
        `/sites/${siteId}/submissions`,
      ),
  },
};
